import { hmac } from "@noble/hashes/hmac";
import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, hexToBytes, randomBytes, utf8ToBytes } from "@noble/hashes/utils";
import { x25519 } from "@noble/curves/ed25519";
import {
  decryptPayload,
  encryptPayload,
  fromB64Url,
  isHex64,
  toB64Url,
  type Envelope,
} from "./crypto.js";
import { isValidRelayUrl } from "./invite.js";

// Nearby join: add several people at one table in one go.
//
// The rule that shapes everything here: THE GROUP KEY NEVER TRAVELS OVER THE
// AIR. Sound does not respect line of sight the way a QR code does, so anyone
// in earshot hears the broadcast. What goes out is a rendezvous pointer only.
// The key is handed over the relay, sealed to one specific person's ephemeral
// key, and only after the host taps approve.
//
// Beacon (25 bytes, about 1.6 seconds of tone):
//   [version 1][session token 16][host commitment 8]
//
// Everything else happens on a throwaway relay topic derived from the token:
//
//   host   -> announce {eph}                sealed with the token key
//   joiner -> request  {eph, pub, name}     sealed with the token key
//   host   -> grant    {group key, ...}     sealed with ECDH(host, that joiner)
//
// The commitment is what stops someone in earshot posing as the host: a joiner
// only accepts the announce whose ephemeral key the beacon already committed
// to, so only the real host can seal a grant that joiner will open. Approval
// itself authors an ordinary signed member_add, so no new op kind and no
// OP_VERSION bump.
//
// Known and accepted leak: an eavesdropper who decodes the beacon holds the
// token key, so they can read the display names in join requests and can post
// requests of their own. They see no group key, no ledger, and no history, and
// their request lands in the host's approve list like anyone else's. The
// confirmation code is what keeps them from being waved through under someone
// else's name.

export const NEARBY_VERSION = 1;
export const SESSION_TOKEN_BYTES = 16;
export const COMMITMENT_BYTES = 8;
export const BEACON_BYTES = 1 + SESSION_TOKEN_BYTES + COMMITMENT_BYTES;
export const CONFIRMATION_DIGITS = 4;

// A session is short by design: the topic is public to earshot, so the window
// in which anyone can post to it should close quickly.
export const SESSION_TTL_MS = 2 * 60_000;
export const MAX_PENDING_REQUESTS = 12;

function kdf(key: Uint8Array, label: string): Uint8Array {
  return hmac(sha256, key, utf8ToBytes(label));
}

// ---------------------------------------------------------------------------
// Session material
// ---------------------------------------------------------------------------

export interface EphemeralKeyPair {
  privKey: Uint8Array; // 32 bytes, never leaves the device and dies with the session
  pubKey: Uint8Array; // 32 bytes
}

export function generateSessionToken(): Uint8Array {
  return randomBytes(SESSION_TOKEN_BYTES);
}

export function generateEphemeral(): EphemeralKeyPair {
  const privKey = x25519.utils.randomPrivateKey();
  return { privKey, pubKey: x25519.getPublicKey(privKey) };
}

/** Symmetric key everyone who heard the beacon shares. Guards the rendezvous, not the group. */
export function deriveTokenKey(token: Uint8Array): Uint8Array {
  if (token.length !== SESSION_TOKEN_BYTES) throw new Error("bad session token");
  return kdf(token, "psplit/nearby/token-key/v1");
}

/** The throwaway relay mailbox for one session. Unrelated to the group's own topic. */
export function deriveNearbyTopic(token: Uint8Array): string {
  return bytesToHex(kdf(deriveTokenKey(token), "psplit/nearby/topic/v1"));
}

/** Binds the beacon to one host ephemeral key, so nobody in earshot can pose as the host. */
export function hostCommitment(token: Uint8Array, hostEphPub: Uint8Array): Uint8Array {
  if (hostEphPub.length !== 32) throw new Error("bad host ephemeral key");
  return hmac(sha256, deriveTokenKey(token), hostEphPub).subarray(0, COMMITMENT_BYTES);
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= (a[i] as number) ^ (b[i] as number);
  return diff === 0;
}

export function hostCommitmentMatches(
  token: Uint8Array,
  commitment: Uint8Array,
  hostEphPub: Uint8Array,
): boolean {
  return sameBytes(commitment, hostCommitment(token, hostEphPub));
}

// ---------------------------------------------------------------------------
// The beacon, the only thing that goes over the air
// ---------------------------------------------------------------------------

export function encodeBeacon(token: Uint8Array, hostEphPub: Uint8Array): Uint8Array {
  if (token.length !== SESSION_TOKEN_BYTES) throw new Error("bad session token");
  const out = new Uint8Array(BEACON_BYTES);
  out[0] = NEARBY_VERSION;
  out.set(token, 1);
  out.set(hostCommitment(token, hostEphPub), 1 + SESSION_TOKEN_BYTES);
  return out;
}

export interface Beacon {
  token: Uint8Array;
  commitment: Uint8Array;
}

export function decodeBeacon(bytes: Uint8Array): Beacon | null {
  if (bytes.length !== BEACON_BYTES) return null;
  if (bytes[0] !== NEARBY_VERSION) return null;
  return {
    token: bytes.slice(1, 1 + SESSION_TOKEN_BYTES),
    commitment: bytes.slice(1 + SESSION_TOKEN_BYTES),
  };
}

// ---------------------------------------------------------------------------
// Per-joiner pairing
// ---------------------------------------------------------------------------

/** Key shared by exactly one host and one joiner. This is what protects the group key. */
export function derivePairKey(
  token: Uint8Array,
  myPriv: Uint8Array,
  theirPub: Uint8Array,
): Uint8Array {
  const shared = x25519.getSharedSecret(myPriv, theirPub);
  const salted = new Uint8Array(shared.length + token.length);
  salted.set(shared, 0);
  salted.set(token, shared.length);
  return kdf(salted, "psplit/nearby/pair-key/v1");
}

/**
 * The digits both people compare before the host approves. Derived from the
 * pair key, so it can only agree if the two devices really did exchange with
 * each other. This is what stops someone in earshot being waved through under
 * a name the host was expecting.
 */
export function confirmationCode(pairKey: Uint8Array): string {
  const d = kdf(pairKey, "psplit/nearby/confirm/v1");
  let n = 0;
  for (let i = 0; i < 4; i++) n = (n << 8) | (d[i] as number);
  return String(Math.abs(n) % 10 ** CONFIRMATION_DIGITS).padStart(CONFIRMATION_DIGITS, "0");
}

// ---------------------------------------------------------------------------
// Messages on the session topic
// ---------------------------------------------------------------------------

export interface NearbyAnnounce {
  t: "nearby-announce";
  v: 1;
  eph: string; // host ephemeral pubkey, hex
}

export interface NearbyRequest {
  t: "nearby-request";
  v: 1;
  eph: string; // joiner ephemeral pubkey, hex
  pub: string; // joiner Ed25519 identity pubkey, hex: this is the member id
  name: string;
}

export interface NearbyGrant {
  t: "nearby-grant";
  v: 1;
  groupId: string;
  key: string; // group key, base64url, sealed to one joiner and nobody else
  epoch: number; // which key epoch that is
  relayUrl: string;
  name: string; // group display name
}

export const MAX_NEARBY_NAME = 64;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseAnnounce(value: unknown): NearbyAnnounce | null {
  if (!isRecord(value) || value["t"] !== "nearby-announce" || value["v"] !== 1) return null;
  const eph = value["eph"];
  if (!isHex64(eph)) return null;
  return { t: "nearby-announce", v: 1, eph };
}

export function parseRequest(value: unknown): NearbyRequest | null {
  if (!isRecord(value) || value["t"] !== "nearby-request" || value["v"] !== 1) return null;
  const eph = value["eph"];
  const pub = value["pub"];
  const name = value["name"];
  if (!isHex64(eph) || !isHex64(pub)) return null;
  if (typeof name !== "string" || name.length > MAX_NEARBY_NAME) return null;
  return { t: "nearby-request", v: 1, eph, pub, name };
}

export function parseGrant(value: unknown): NearbyGrant | null {
  if (!isRecord(value) || value["t"] !== "nearby-grant" || value["v"] !== 1) return null;
  const groupId = value["groupId"];
  const key = value["key"];
  const relayUrl = value["relayUrl"];
  const name = value["name"];
  if (!isHex64(groupId)) return null;
  if (typeof key !== "string") return null;
  let keyBytes: Uint8Array;
  try {
    keyBytes = fromB64Url(key);
  } catch {
    return null;
  }
  if (keyBytes.length !== 32) return null;
  if (typeof relayUrl !== "string" || !isValidRelayUrl(relayUrl)) return null;
  if (typeof name !== "string" || name.length > MAX_NEARBY_NAME) return null;
  const epoch = value["epoch"] ?? 0;
  if (typeof epoch !== "number" || !Number.isSafeInteger(epoch) || epoch < 0) return null;
  return { t: "nearby-grant", v: 1, groupId, key, epoch, relayUrl, name };
}

async function seal(key: Uint8Array, msg: unknown): Promise<Envelope> {
  return encryptPayload(key, utf8ToBytes(JSON.stringify(msg)));
}

async function open(key: Uint8Array, envelope: Envelope): Promise<unknown> {
  const bytes = await decryptPayload(key, envelope);
  return JSON.parse(new TextDecoder().decode(bytes));
}

/**
 * Try to open an envelope, returning null instead of throwing. A session topic
 * carries envelopes under several different keys plus anything a stranger
 * posted, so failing to decrypt is the normal case, not an error.
 */
export async function tryOpen(key: Uint8Array, envelope: Envelope): Promise<unknown> {
  try {
    return await open(key, envelope);
  } catch {
    return null;
  }
}

export function sealAnnounce(tokenKey: Uint8Array, hostEphPub: Uint8Array): Promise<Envelope> {
  const msg: NearbyAnnounce = { t: "nearby-announce", v: 1, eph: bytesToHex(hostEphPub) };
  return seal(tokenKey, msg);
}

export function sealRequest(
  tokenKey: Uint8Array,
  joinerEphPub: Uint8Array,
  identityPub: string,
  name: string,
): Promise<Envelope> {
  const msg: NearbyRequest = {
    t: "nearby-request",
    v: 1,
    eph: bytesToHex(joinerEphPub),
    pub: identityPub,
    name: name.slice(0, MAX_NEARBY_NAME),
  };
  return seal(tokenKey, msg);
}

export interface GrantInput {
  groupId: string;
  groupKey: Uint8Array;
  epoch: number;
  relayUrl: string;
  name: string;
}

export function sealGrant(pairKey: Uint8Array, input: GrantInput): Promise<Envelope> {
  const msg: NearbyGrant = {
    t: "nearby-grant",
    v: 1,
    groupId: input.groupId,
    key: toB64Url(input.groupKey),
    epoch: input.epoch,
    relayUrl: input.relayUrl,
    name: input.name.slice(0, MAX_NEARBY_NAME),
  };
  return seal(pairKey, msg);
}

/** Host side: read every announce/request envelope a session topic holds. */
export async function readAnnounce(
  tokenKey: Uint8Array,
  envelope: Envelope,
): Promise<NearbyAnnounce | null> {
  return parseAnnounce(await tryOpen(tokenKey, envelope));
}

export async function readRequest(
  tokenKey: Uint8Array,
  envelope: Envelope,
): Promise<NearbyRequest | null> {
  return parseRequest(await tryOpen(tokenKey, envelope));
}

export async function readGrant(
  pairKey: Uint8Array,
  envelope: Envelope,
): Promise<NearbyGrant | null> {
  return parseGrant(await tryOpen(pairKey, envelope));
}

export function ephemeralPubFromHex(hex: string): Uint8Array {
  if (!isHex64(hex)) throw new Error("bad ephemeral key");
  return hexToBytes(hex);
}
